import { NavLink, Link, useNavigate } from "react-router-dom";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  LayoutDashboard, Car, Users, ShieldCheck, Settings as SettingsIcon,
  ScrollText, Bell, Search, Menu, X, LogOut, User, MessageCircle,
  UserSquare2, Receipt, Wallet, CreditCard, FileText, VolumeX, BarChart3, ChevronsLeft, Globe, PenLine, Share2, Megaphone, Lock, Inbox,
} from "lucide-react";
import { useLicense } from "../lib/license";
import { UpgradeDialog } from "./UpgradeDialog";
import { LicenseBanner } from "./LicenseBanner";
import { BranchSwitcher } from "./BranchSwitcher";
import { usePickedBranch } from "../lib/branches";

// true on small phones, so the search box can use a shorter hint that fits
function useNarrow(px) {
  const q = `(max-width: ${px}px)`;
  const [on, setOn] = useState(() => window.matchMedia(q).matches);
  useEffect(() => {
    const mq = window.matchMedia(q);
    const fn = () => setOn(mq.matches);
    mq.addEventListener("change", fn);
    return () => mq.removeEventListener("change", fn);
  }, [q]);
  return on;
}
import { useAuth } from "../lib/auth";
import { useSettings } from "../lib/settings";
import { useChat } from "../lib/chat";
import { useT, trText, lbl } from "../lib/i18n";
import { useLocation } from "react-router-dom";
import { api } from "../lib/api";
import PoweredBy from "./PoweredBy";
import { canOpen } from "../lib/access";
import { BrandLogo } from "./BrandLogo";

import { tr } from "../lib/i18n";
const GROUPS = [
  { label: "group.showroom", items: [
    { to: "/dashboard", key: "nav.dashboard", icon: LayoutDashboard, module: null },
    { to: "/inventory", key: "nav.inventory", icon: Car, module: "vehicles" },
    { to: "/leads", key: "ld.nav", icon: Inbox, module: "customers" },
    { to: "/customers", key: "nav.customers", icon: UserSquare2, module: "customers" },
  ]},
  { label: "group.finance", items: [
    { to: "/sales", key: "nav.sales", icon: Receipt, module: "sales" },
    { to: "/payments", key: "nav.payments", icon: Wallet, module: "payments" },
    { to: "/online-payments", key: "nav.online_payments", icon: CreditCard, module: "payments", feature: "online_payments" },
    { to: "/documents", key: "nav.documents", icon: FileText, module: "documents" },
    { to: "/forms", key: "nav.forms", icon: PenLine, module: "forms" },
    { to: "/reports", key: "nav.reports", icon: BarChart3, module: "reports" },
    { to: "/commissions", key: "nav.commissions", icon: BarChart3, module: null, roles: ["admin", "management"] },
  ]},
  { label: "group.engage", items: [
    { to: "/live-chat", key: "nav.livechat", icon: MessageCircle, module: "chat", showBadge: true },
    { to: "/social/suggestions", key: "nav.social_suggestions", icon: Megaphone, module: "social", feature: "social_posting" },
    { to: "/social/accounts", key: "nav.social_accounts", icon: Share2, module: "social_accounts", feature: "social_posting" },
  ]},
  { label: "group.admin", items: [
    { to: "/users", key: "nav.users", icon: Users, module: "users" },
    { to: "/permissions", key: "nav.permissions", icon: ShieldCheck, module: "users", adminOnly: true },
    { to: "/activity", key: "nav.activity", icon: ScrollText, module: null, roles: ["admin", "management"] },
    { to: "/settings", key: "nav.settings", icon: SettingsIcon, module: "settings" },
  ]},
];

export default function AppLayout({ children }) {
  const { user, logout, can } = useAuth();
  const { settings } = useSettings();
  const { t, lang, setLang } = useT();
  const chat = useChat();
  const nav = useNavigate();
  const [drawer, setDrawer] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem("sb_collapsed") === "1"; } catch { return false; }
  });
  const [search, setSearch] = useState("");
  const branchKey = usePickedBranch();  // pages reload their data when the branch changes
  const narrow = useNarrow(480);
  const [results, setResults] = useState(null);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifs, setNotifs] = useState({ items: [], unread: 0 });
  const [userMenu, setUserMenu] = useState(false);
  const { lic, has } = useLicense();
  const [upgrade, setUpgrade] = useState(null);

  useEffect(() => { try { localStorage.setItem("sb_collapsed", collapsed ? "1" : "0"); } catch {} }, [collapsed]);

  const groups = useMemo(() => GROUPS.map((g) => ({
    ...g,
    items: g.items.filter((n) => canOpen(n.to, user, can)),
  })).filter((g) => g.items.length), [can, user]);

  useEffect(() => {
    let cancelled = false;
    const timer = setTimeout(async () => {
      if (search.trim().length < 2) { setResults(null); return; }
      try {
        const { data } = await api.get("/search", { params: { q: search.trim() } });
        if (!cancelled) setResults(data);
      } catch { setResults(null); }
    }, 250);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [search]);

  const seenNotifs = useRef(null);
  const chatCtx = chat;
  const loadNotifs = async () => {
    try {
      const { data } = await api.get("/notifications");
      setNotifs(data);
      const leads = (data.items || []).filter((n) => n.module === "leads" && !n.read).map((n) => n.id);
      if (seenNotifs.current && leads.some((id) => !seenNotifs.current.has(id)) && can("customers", "view")) chatCtx?.chime?.();
      seenNotifs.current = new Set([...(seenNotifs.current || []), ...leads]);
    } catch {}
  };
  useEffect(() => {
    loadNotifs();
    const id = setInterval(loadNotifs, 20000);
    return () => clearInterval(id);
  }, []);

  const sbWidth = collapsed ? 68 : 248;
  const sbLight = false; // staff sidebar is always Ink
  const loc = useLocation();
  const sbLogo = sbLight
    ? (settings?.logo_light_url || settings?.logo_dark_url)
    : (settings?.logo_dark_url || settings?.logo_light_url);

  return (
    <div className="min-h-screen flex" style={{ background: "var(--ground)" }}>
      {/* Sidebar */}
      <aside
        data-testid="app-sidebar"
        className={`sidebar-shell fixed lg:sticky lg:top-0 z-40 h-screen flex-shrink-0 flex flex-col transition-transform ${drawer ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}
        style={{ width: sbWidth, background: "var(--s, #141416)", color: "var(--s-ink, #fff)" }}
      >
        <div className="sidebar-rule px-4 py-5 flex items-center gap-3 border-b min-h-[76px]">
          {(sbLogo || !collapsed) && (
            <BrandLogo src={sbLogo} name={settings?.showroom_name || tr("x.showroom")} testid="sidebar" imgStyle={{ height: 40, width: collapsed ? 36 : "auto", maxWidth: "100%", objectFit: "contain", display: "block" }} nameClassName="font-medium text-sm truncate" nameStyle={{ color: "var(--s-ink)" }} />
          )}
          <button data-testid="close-drawer" aria-label={tr("x.close_menu")} className="sidebar-muted lg:hidden ms-auto w-11 h-11 flex items-center justify-center" onClick={() => setDrawer(false)}>
            <X size={18} />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto py-3">
          {groups.map((g) => (
            <div key={g.label} className="mb-2">
              {!collapsed && <div className="sidebar-group-label">{t(g.label)}</div>}
              {g.items.map((it) => {
                const badge = it.showBadge ? (chat?.counts?.unassigned || 0) + (chat?.counts?.mine || 0) : 0;
                if (it.feature && !has(it.feature)) return (
                  <button key={it.to} type="button" className="sidebar-item w-full text-start opacity-70" data-testid={`nav-${it.key.split('.').pop()}`} data-locked="1"
                    onClick={() => { setDrawer(false); setUpgrade(it.feature); }} title={collapsed ? t(it.key) : undefined}>
                    <it.icon size={16} strokeWidth={1.75} />
                    {!collapsed && <span className="flex-1">{t(it.key)}</span>}
                    {!collapsed && <Lock size={13} data-testid={`nav-lock-${it.key.split('.').pop()}`} />}
                  </button>
                );
                return (
                  <NavLink
                    key={it.to}
                    to={it.to}
                    data-testid={`nav-${it.key.split('.').pop()}`}
                    className={({ isActive }) => `sidebar-item ${isActive ? "active" : ""}`}
                    onClick={() => setDrawer(false)}
                    title={collapsed ? t(it.key) : undefined}
                  >
                    <it.icon size={16} strokeWidth={1.75} />
                    {!collapsed && <span className="flex-1">{t(it.key)}</span>}
                    {!collapsed && badge > 0 && (
                      <span style={{ background: "var(--accent)", color: "#fff", padding: "0.1rem 0.4rem", fontSize: "0.68rem" }} className="font-medium">
                        {badge}
                      </span>
                    )}
                  </NavLink>
                );
              })}
            </div>
          ))}
        </nav>

        <div className="sidebar-rule px-4 py-3 border-t flex items-center justify-between">
          {!collapsed && <PoweredBy variant="sidebar" />}
          <button
            data-testid="sidebar-collapse"
            onClick={() => setCollapsed((v) => !v)}
            className="sidebar-muted ml-auto flip-rtl"
            aria-label={t("top.collapse")}
          >
            <ChevronsLeft size={16} style={{ transform: collapsed ? "scaleX(-1)" : "none" }} />
          </button>
        </div>
      </aside>

      {/* Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Topbar */}
        <header
          className="sticky top-0 z-30 flex flex-wrap sm:flex-nowrap items-center px-4 md:px-6 gap-x-3 gap-y-2 py-2 sm:py-0"
          style={{ minHeight: 72, background: "var(--ground)", borderBottom: "2px solid var(--text)" }}
        >
          <button data-testid="open-drawer" aria-label={tr("x.open_menu")} className="lg:hidden w-11 h-11 flex items-center justify-center flex-shrink-0" onClick={() => setDrawer(true)}>
            <Menu size={20} strokeWidth={1.75} />
          </button>

          {/* phones: search gets its own full-width row so the hint is readable */}
          <div className="relative flex-1 min-w-0 max-w-xl order-last basis-full sm:order-none sm:basis-auto">
            <div className="flex items-center gap-2 h-10 px-3 min-w-0" style={{ background: "#fff", border: "1px solid var(--divider)" }}>
              <Search size={15} strokeWidth={1.75} className="opacity-60" />
              <input
                data-testid="global-search-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t(narrow ? "top.search_short" : "top.search")}
                className="flex-1 min-w-0 w-full bg-transparent text-sm outline-none"
              />
              <kbd dir="ltr" className="hidden sm:inline text-[10px] px-1.5 py-0.5" style={{ border: "1px solid var(--divider)", color: "var(--ds-ink-muted)" }}>⌘K</kbd>
            </div>
            {results && search.length >= 2 && (
              <div data-testid="global-search-results" className="absolute top-11 start-0 end-0 bg-white has-shadow p-3 max-h-96 overflow-y-auto" style={{ border: "1px solid var(--divider)" }}>
                <div className="kicker mb-2">{tr("x.vehicles")}{results.vehicles.length})</div>
                <ul className="space-y-1">
                  {results.vehicles.slice(0, 10).map((v) => (
                    <li key={v.id}>
                      <button className="w-full text-start px-2 py-2 text-sm hover:bg-[var(--surface)]"
                        onClick={() => { setSearch(""); setResults(null); nav(`/inventory/${v.id}`); }}>
                        <span className="mono text-xs me-2 opacity-60">{v.stock_number}</span>
                        {v.make} {v.model} {v.model_year}
                      </button>
                    </li>
                  ))}
                  {results.vehicles.length === 0 && <li className="text-sm opacity-60 px-2">{tr("x.no_vehicles")}</li>}
                </ul>
              </div>
            )}
          </div>

          <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
            <BranchSwitcher />
            {lic?.mode === "dev" && (
              <span className="hidden md:inline text-[10px] uppercase tracking-widest px-2 py-1" style={{ border: "1px dashed var(--divider)", color: "var(--text-muted)" }} data-testid="license-dev-badge">
                {tr("lic.dev_badge")}
              </span>
            )}
            <button
              data-testid="lang-switch"
              onClick={() => { const l = lang === "en" ? "ar" : "en"; api.post("/auth/update-profile", { language: l }).finally(() => setLang(l)); }}
              aria-label={t("top.lang_switch")}
              className="h-11 min-w-[44px] px-2 sm:px-3 text-xs font-medium flex items-center justify-center gap-1.5"
              style={{ border: "1px solid var(--text)" }}
            >
              <Globe size={14} strokeWidth={1.75} />
              <span className="hidden sm:inline">{t("top.lang_switch")}</span>
            </button>

            <div className="relative">
              {chat?.soundOn && !chat?.audioUnlocked && (
                <span className="absolute bottom-1 start-1 opacity-70 leading-none pointer-events-none" title={tr("x.click_anywhere_to_enable_sound")} aria-label={tr("x.click_anywhere_to_enable_sound")} data-testid="enable-sound-hint">
                  <VolumeX size={12} />
                </span>
              )}
              <button data-testid="notifications-bell" onClick={() => setNotifOpen((v) => !v)}
                aria-label={tr("x.notifications")}
                className="w-11 h-11 flex items-center justify-center relative hover:bg-[var(--surface)]">
                <Bell size={16} strokeWidth={1.75} />
                {notifs.unread > 0 && <span className="absolute top-2.5 end-2.5 w-2 h-2 dot-round" style={{ background: "var(--accent)" }} data-testid="notifications-unread-dot" />}
              </button>
              {notifOpen && (
                <div data-testid="notifications-popover" className="absolute end-0 top-11 w-80 bg-white has-shadow p-3 max-h-96 overflow-y-auto" style={{ border: "1px solid var(--divider)" }}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="kicker">{tr("x.notifications")}</div>
                    <button className="text-xs" style={{ color: "var(--accent-700)" }}
                      onClick={async () => { await api.post("/notifications/read-all"); loadNotifs(); }}>
                      {tr("x.mark_all_read")}
                    </button>
                  </div>
                  <ul className="space-y-2">
                    {notifs.items.length === 0 && <li className="text-sm opacity-60">{tr("x.no_notifications")}</li>}
                    {notifs.items.slice(0, 10).map((n) => (
                      <li key={n.id} className={`p-2 text-sm ${n.read ? "" : "bg-[var(--surface)]"}`}>
                        <div className="font-medium">{n.module === "leads" && n.entity_id ? <Link to={`/leads/${n.entity_id}`} onClick={() => { api.post(`/notifications/${n.id}/read`).catch(() => {}); setNotifOpen(false); }} className="hover:underline" data-testid={`notif-lead-${n.id}`}>{trText(n.title)}</Link> : trText(n.title)}</div>
                        <div className="text-xs opacity-70">{trText(n.body)}</div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <div className="relative">
              <button data-testid="user-menu-btn" onClick={() => setUserMenu((v) => !v)}
                className="flex items-center gap-2 h-10 px-2 hover:bg-[var(--surface)]">
                <div style={{ width: 28, height: 28, background: "var(--accent)", color: "#fff" }} className="flex items-center justify-center text-xs font-medium">
                  {(user?.full_name || "U").split(" ").map((p) => p[0]).join("").slice(0,2).toUpperCase()}
                </div>
                <div className="hidden md:block text-start">
                  <div className="text-sm font-medium leading-none max-w-[140px] truncate">{user?.full_name}</div>
                  <div className="text-[11px] mt-0.5" style={{ color: "var(--ds-ink-muted)" }}>{lbl(user?.role)}</div>
                </div>
              </button>
              {userMenu && (
                <div className="absolute end-0 top-11 w-56 bg-white has-shadow p-2" style={{ border: "1px solid var(--divider)" }}>
                  <div className="px-3 py-2 border-b border-[var(--divider-soft)]">
                    <div className="text-sm font-medium truncate">{user?.full_name}</div>
                    <div className="text-xs opacity-60 truncate">{user?.email}</div>
                  </div>
                  {["admin", "management"].includes(user?.role) && (
                    <button data-testid="menu-my-plan" className="w-full text-start px-3 py-2 text-sm hover:bg-[var(--surface)]"
                      onClick={() => { setUserMenu(false); nav("/plan"); }}>
                      {tr("lic.my_plan")}
                    </button>
                  )}
                  <button data-testid="menu-change-password" className="w-full text-start px-3 py-2 text-sm hover:bg-[var(--surface)]"
                    onClick={() => { setUserMenu(false); nav("/change-password"); }}>
                    {t("top.changepw")}
                  </button>
                  <button data-testid="menu-logout" className="w-full flex items-center gap-2 text-start px-3 py-2 text-sm hover:bg-[var(--surface)]"
                    style={{ color: "var(--status-overdue)" }}
                    onClick={async () => { await logout(); nav("/login"); }}>
                    <LogOut size={14} strokeWidth={1.75} /> {t("top.signout")}
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        <LicenseBanner />
        <main className="flex-1 p-4 md:p-8 overflow-x-hidden"><div key={`${loc.pathname}|${branchKey}`} className="ds-page">{children}</div></main>
      </div>
      <UpgradeDialog flag={upgrade} onClose={() => setUpgrade(null)} />

      {drawer && <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setDrawer(false)} />}
    </div>
  );
}
